import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";

export interface DashboardStats {
  // Existentes
  submissions: number;
  withReport: number;
  pending: number;
  clients: number;
  launches: number;

  // Nuevas métricas
  submissionsToday: number;
  submissionsThisWeek: number;
  conversionRate: number;
  avgReportTime: number | null;

  // JotForm sync
  syncedForms: number;
  lastSyncAt: string | null;
}

export function useDashboardStats() {
  const { currentProject } = useProject();
  const projectId = currentProject === '3m' ? 1 : 2;

  return useQuery({
    queryKey: ['dashboard-stats', currentProject],
    queryFn: async (): Promise<DashboardStats> => {
      // Ejecutar todas las queries en paralelo
      const [
        submissionCountResult,
        withReportLegacyResult,
        withReportQueueResult,
        clientCountResult,
        launchCountResult,
        todayCountResult,
        weekCountResult,
        avgTimeResult,
        syncedFormsResult,
        lastSyncResult
      ] = await Promise.all([
        // Total submissions
        supabase
          .from('jotform_submissions')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', projectId),

        // Submissions con reporte (legacy report_url)
        supabase
          .from('jotform_submissions')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .not('report_url', 'is', null),

        // Submissions con reporte (new queue system - distinct submission_ids)
        supabase
          .from('generated_reports')
          .select('submission_id', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .eq('status', 'completed')
          .eq('is_outdated', false),

        // Total clientes
        supabase
          .from('clientes')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', projectId),

        // Total lanzamientos
        supabase
          .from('form_launches')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', projectId),

        // Submissions hoy (últimas 24 horas)
        supabase
          .from('jotform_submissions')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .gte('jotform_created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()),

        // Submissions esta semana (últimos 7 días)
        supabase
          .from('jotform_submissions')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .gte('jotform_created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),

        // Tiempo promedio de generación (usando RPC o query directa)
        supabase
          .from('jotform_submissions')
          .select('jotform_created_at, report_generated_at')
          .eq('project_id', projectId)
          .not('report_generated_at', 'is', null)
          .limit(100), // Limitar para performance

        // Formularios sincronizados
        supabase
          .from('jotform_form_mappings')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', projectId),

        // Último sync
        supabase
          .from('jotform_submissions')
          .select('synced_at')
          .eq('project_id', projectId)
          .order('synced_at', { ascending: false })
          .limit(1)
      ]);

      const submissionCount = submissionCountResult.count || 0;
      // Combine both legacy (report_url) and new queue system counts
      // Use max to avoid undercounting during migration
      const legacyReportCount = withReportLegacyResult.count || 0;
      const queueReportCount = withReportQueueResult.count || 0;
      const withReportCount = Math.max(legacyReportCount, queueReportCount);

      // Calcular tiempo promedio de generación
      let avgReportTime: number | null = null;
      if (avgTimeResult.data && avgTimeResult.data.length > 0) {
        const times = avgTimeResult.data
          .filter(s => s.jotform_created_at && s.report_generated_at)
          .map(s => {
            const created = new Date(s.jotform_created_at).getTime();
            const generated = new Date(s.report_generated_at).getTime();
            return (generated - created) / (1000 * 60 * 60); // En horas
          });

        if (times.length > 0) {
          avgReportTime = Math.round((times.reduce((a, b) => a + b, 0) / times.length) * 10) / 10;
        }
      }

      // Calcular tasa de conversión
      const conversionRate = submissionCount > 0
        ? Math.round((withReportCount / submissionCount) * 100)
        : 0;

      return {
        submissions: submissionCount,
        withReport: withReportCount,
        pending: submissionCount - withReportCount,
        clients: clientCountResult.count || 0,
        launches: launchCountResult.count || 0,
        submissionsToday: todayCountResult.count || 0,
        submissionsThisWeek: weekCountResult.count || 0,
        conversionRate,
        avgReportTime,
        syncedForms: syncedFormsResult.count || 0,
        lastSyncAt: lastSyncResult.data?.[0]?.synced_at || null
      };
    },
    enabled: !!currentProject,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000     // 5 minutos
  });
}
